import { AutocompleteChangeReason } from "@mui/material/Autocomplete";
import { EMPTY_URL } from "csi/constants";
import csiPrefs from "csi/prefs";
import { PreferenceView } from "csi/prefs/PreferenceView";
import { TYPE_VIEW } from "csi/reflection/reflectionConstants";
import { getNodeNames } from "csi/service/nodeService";
import { CustomViewsStore } from "csi/store/CustomViews";
import { StoreQuery } from "csi/store/Request";
import { cnl } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { ComponentItem } from "designSystem/types/component-item-types";
import { compact, flatten } from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { createContext, useContext } from "react";
import { DUMMY } from "src/c8-platform/type/node";
import CustomViewDialogStore from "../CustomViewDialogStore";
import { ICustomViewUtil } from "../customViewService";
import { ICustomViewDataSheetOptions } from "../options/CustomViewOptions";

export type NestedViewOptions = Pick<ICustomViewDataSheetOptions, "nestedViews">;

export interface CompositeViewItem {
	/** Id of the view */
	readonly id: string;

	/** Name of the view */
	readonly name: string;

	/** Name of the context */
	readonly context: string;

	/** store of the view */
	readonly store?: CustomViewsStore;
}

export class CustomViewCompositeViewItem implements CompositeViewItem {
	/** options for the custom view dropdown */
	@observable.ref options: ComponentItem[];

	/** Name of the context */
	readonly context: string;

	/** Id of the view */
	readonly id: string;

	/** Name of the view */
	readonly name: string;

	/** Query object for the store */
	private readonly queryObject: StoreQuery;

	/** selected custom view from the dropdown */
	@observable.ref selectedCustomView: ComponentItem;

	/** store of the view */
	store?: CustomViewsStore;

	/** visibility of the view */
	@observable visible: boolean;

	/** indicate if has custom views */
	get hasCustomViews(): boolean {
		return !!this.store;
	}

	/** collect invalid views during validation */
	collectInvalidViews = async (customViewDialogStore: CustomViewDialogStore): Promise<string[]> => {
		const { prefParty } = customViewDialogStore.settingStore;
		const { store } = this;
		const invalidViews = [];

		if (!store) {
			// for example, the Canvas case
			return invalidViews;
		}

		const { id } = this.selectedCustomView;

		if (id != EMPTY_URL) {
			// remove the old entry so that we can get the latest info from server
			store.remove(id);
		}

		const selectedPreferenceView = await store.get(id);

		if (!selectedPreferenceView.canBeAccessedWith(prefParty)) {
			invalidViews.push(selectedPreferenceView.name);
		}

		return invalidViews;
	};

	/** handles when a user clicks the custom view dropdown */
	@action customViewDropDownClickHandler = async (name: string): Promise<void> => {
		const results = await this.store.asyncQuery(this.queryObject);
		this.updateCustomViewOption(results);
	};

	/** handles when a user clicks the custom view dropdown and make selection update */
	@action customViewSelectChangeHandler = (event: React.ChangeEvent<{}>, item: ComponentItem, reason: AutocompleteChangeReason): void => {
		this.selectedCustomView = item;
	};

	/** handles custom view options update */
	@action updateCustomViewOption = (customViewItems: PreferenceView[]): void => {
		this.options = customViewItems?.map((view) => ({ id: view.urlNode, name: view.name }));
	};

	/** handles when a user clicks the visible checkbox */
	@action visibleCheckBoxClickHandler = (value: boolean): void => {
		this.visible = value;
	};

	constructor(compositeViewItem: CompositeViewItem, visible: boolean, selectedCustomView: ComponentItem) {
		makeObservable(this);
		const { id, name, context, store } = compositeViewItem;
		this.id = id;
		this.name = name;
		this.context = context;
		this.selectedCustomView = selectedCustomView;
		this.store = store;
		this.visible = visible;

		const customViews = store?.getLoadedViews();

		if (customViews?.length) {
			this.updateCustomViewOption(customViews);
		}

		this.queryObject = this.context ? csiPrefs.getStoreSelectQuery(this.context) : null;
	}
}

/**
 * This store contains information about composite view
 */

export default class CustomViewCompositeStore extends ReactionStore implements ICustomViewUtil<NestedViewOptions> {
	/** custom view dialog store */
	readonly customViewDialogStore: CustomViewDialogStore;

	readonly #OOTBNestedViewMap: Record<string, cnl> = {};

	/** indicate if there's any permission violation */
	@observable permissionViolation: boolean = false;

	/** current settings container */
	readonly views: CustomViewCompositeViewItem[] = [];

	/** names of the violation views */
	@observable violationViewNames: string = "";

	/** values of custom view options from composite tab */
	@computed get optionValues(): NestedViewOptions {
		const nestedViews = {};

		this.views.forEach(({ id, selectedCustomView, visible }) => {
			if (visible) {
				nestedViews[id] = selectedCustomView?.id || DUMMY;
			}
		});

		return { nestedViews };
	};

	/** parameters for the permission error resource */
	get permissionErrorRes(): string[] {
		const { violationViewNames } = this;
		const { shareWith } = this.customViewDialogStore.settingStore;
		return [violationViewNames, shareWith];
	}

	/** @override */
	@action onChangeOptions = async (options: NestedViewOptions): Promise<void> => {
		const { processNonDefaultChildrenViews } = this;
		const urlsToRetrieveName = [];
		const finalOptions = options || this.#OOTBNestedViewMap;

		this.views.forEach((data, i) => {
			const selectedNestedViewId = finalOptions[data.id];
			data.visible = !!selectedNestedViewId;

			if (selectedNestedViewId && selectedNestedViewId !== EMPTY_URL) {
				urlsToRetrieveName.push(selectedNestedViewId);
			}
		});

		await processNonDefaultChildrenViews(urlsToRetrieveName, finalOptions);
	};

	/** process non default/OOTB views */
	processNonDefaultChildrenViews = async (urls: cnl[], options: NestedViewOptions): Promise<void> => {
		const { loc } = this.customViewDialogStore;
		const nameMap = await getNodeNames(urls, TYPE_VIEW);
		nameMap.set(EMPTY_URL, loc.resource("ViewDefault"));

		this.views.forEach((data, i) => {
			if (data.options) {
				const selectedNestedViewId = options[data.id];

				// the newly selected composite view doesn't include this view(it was hidden), just skip
				if (!selectedNestedViewId) {
					return;
				}

				let target = data.options.find((option) => option.id === selectedNestedViewId);

				runInAction(() => {
					if (target) {
						data.selectedCustomView = target;
					} else {
						target = { id: selectedNestedViewId, name: nameMap.get(selectedNestedViewId) };
						data.options = [target];
						data.selectedCustomView = target;
					}
				});
			}
		});
	};

	/** reset violation */
	@action resetViolation = (): void => {
		this.permissionViolation = false;
	};

	/** validate the child views permissions */
	validate = async (): Promise<boolean> => {
		const { customViewDialogStore, views } = this;
		const promises = [];

		views.forEach(({ collectInvalidViews }) => {
			promises.push(collectInvalidViews(customViewDialogStore));
		});

		const invalidViews = compact(flatten(await Promise.all(promises)));

		const valid = !invalidViews.length;

		runInAction(() => {
			this.permissionViolation = !valid;
			this.violationViewNames = invalidViews.join(", ");
		});

		return valid;
	};

	constructor(views: CustomViewCompositeViewItem[], customViewDialogStore: CustomViewDialogStore, OOTBNestedViewMap: Record<string, cnl>) {
		super();
		makeObservable(this);

		views.forEach((data, i) => {
			this.views.push(data);
		});

		this.customViewDialogStore = customViewDialogStore;
		this.#OOTBNestedViewMap = OOTBNestedViewMap;
	}
}

export const CustomViewCompositeStoreContext = createContext<CustomViewCompositeStore>(undefined);
export const useCustomViewCompositeStore = (): CustomViewCompositeStore => useContext(CustomViewCompositeStoreContext);